import { useState } from 'react';
import { NavLink } from 'react-router';
import { contactApi } from '@/api/endpoints/contact';
import { ROUTES } from '@/lib/constants';

const COMPANY_LINKS = [
  { label: 'About', path: ROUTES.ABOUT },
  { label: 'Products', path: ROUTES.PRODUCTS },
  { label: 'Technology', path: ROUTES.TECHNOLOGY },
  { label: 'Blog', path: ROUTES.BLOG },
  { label: 'Events', path: ROUTES.EVENTS },
  { label: 'FAQ', path: ROUTES.FAQ },
  { label: 'Contact Us', path: ROUTES.CONTACT },
];

const SOCIAL_LINKS = [
  { label: 'Instagram', href: 'https://www.instagram.com/endoaxis/' },
];

export function Footer() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const currentYear = new Date().getFullYear();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    setError('');
    try {
      await contactApi.newsletter({ firstName, lastName, email });
      setSubmitted(true);
      setFirstName('');
      setLastName('');
      setEmail('');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-ea-02 px-6 py-10 md:px-12">
      <div className="mx-auto max-w-7xl">
        {/* Main footer content */}
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          {/* Newsletter signup */}
          <div className="md:col-span-5">
            <h2 className="mb-3 text-3xl font-normal text-text-light">
              Stay in the loop.
            </h2>
            {submitted ? (
              <p className="max-w-sm text-base text-green-400">
                Thanks for subscribing!
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="max-w-sm space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full border-b border-ea-03 bg-transparent px-2 py-2.5 text-base text-white placeholder-ea-03 outline-none focus:border-white"
                  />
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full border-b border-ea-03 bg-transparent px-2 py-2.5 text-base text-white placeholder-ea-03 outline-none focus:border-white"
                  />
                </div>
                <div className="relative">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border-b border-ea-03 bg-transparent px-2 py-2.5 pr-10 text-base text-white placeholder-ea-03 outline-none focus:border-white"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="absolute bottom-2.5 right-2 text-ea-03 hover:text-white disabled:opacity-50"
                    aria-label="Subscribe"
                  >
                    {isSubmitting ? (
                      <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                        <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="m15.4423,3.65712l0,11.38458m0.93505,-11.43433l-12.31967,0.04975m11.04659,0.27058l-11.04659,11.114"
                          stroke="currentColor"
                          strokeWidth="1.87058"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {error && (
                  <p className="text-sm text-red-400">{error}</p>
                )}
              </form>
            )}
          </div>

          {/* Company links */}
          <div className="md:col-span-3">
            <h3 className="mb-4 text-xs font-normal uppercase tracking-wide text-ea-03">
              Company
            </h3>
            <ul className="space-y-2.5">
              {COMPANY_LINKS.map((link) => (
                <li key={link.path}>
                  <NavLink
                    to={link.path}
                    className="text-base text-text-light hover:text-white"
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Social links */}
          <div className="md:col-span-4">
            <h3 className="mb-4 text-xs font-normal uppercase tracking-wide text-ea-03">
              Social
            </h3>
            <ul className="space-y-2.5">
              {SOCIAL_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-base text-text-light hover:text-white"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Copyright bar */}
        <div className="mt-16 flex flex-col items-start justify-between gap-4 text-sm text-ea-03 md:flex-row md:items-center">
          <span>&copy; {currentYear} EndoAxis</span>
          <a
            href="mailto:cs@endoaxis.com"
            className="text-ea-03 underline hover:text-text-light"
          >
            cs@endoaxis.com
          </a>
          <div className="flex gap-14">
            <NavLink
              to="/terms"
              className="text-ea-03 hover:text-text-light"
            >
              Terms
            </NavLink>
            <NavLink
              to="/privacy"
              className="text-ea-03 hover:text-text-light"
            >
              Privacy
            </NavLink>
          </div>
        </div>

        {/* EndoAxis logo */}
        <div className="mt-4 border-t border-ea-01 pt-4">
          <img
            src="/img/EndoAxis-white.svg"
            alt="EndoAxis"
            className="w-full"
          />
        </div>
      </div>
    </footer>
  );
}
